"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useRouter } from "next/navigation"

import { getNativeBridge } from "@/app/lib/native/bridge"
import { getNativeEnvironment } from "@/app/lib/native/environment"
import {
  type NativeConsentPayload,
  type NativeEnvironment,
  isNativeDeepLinkPayload,
} from "@/app/lib/native/messages"
import {
  GAME_ID,
  NATIVE_COOKIE,
  type NativePlatform,
} from "@/app/lib/native/native-mode"
import {
  dispatchSpaNavigate,
  getTopLevelTab,
} from "@/app/lib/spa-navigation"

interface NativeContextValue {
  /** True when the app is running inside the GamingHub host app's webview. */
  isNative: boolean
  platform: NativePlatform | null
  /** Null until the handshake completes, and in an ordinary browser. */
  environment: NativeEnvironment | null
  consent: NativeConsentPayload | null
  /**
   * Ask the host app to open its menu, so the user can reach the rest of the app.
   * The shell's own navigation bar has a Menu button that does the same thing;
   * this is for menu entry points inside the game content.
   */
  openMenu: () => void
}

const NativeContext = createContext<NativeContextValue>({
  isNative: false,
  platform: null,
  environment: null,
  consent: null,
  openMenu: () => {},
})

/**
 * How long to wait for the bridge before concluding the native flag was wrong.
 * Generous, because this only fires on the recovery path.
 */
const BRIDGE_GRACE_MS = 3000

/**
 * Owns everything about running inside the native shell except auth, which
 * `GamingHubProvider` handles because it already owns that surface.
 *
 * `platform` is seeded from the server so the first paint agrees with SSR — the
 * root layout has already used it to decide whether to render the hosted site
 * chrome, and re-deriving it on the client would risk a hydration mismatch.
 */
export function NativeProvider({
  platform,
  children,
}: {
  platform: NativePlatform | null
  children: React.ReactNode
}) {
  const router = useRouter()

  const [environment, setEnvironment] = useState<NativeEnvironment | null>(null)
  const [consent, setConsent] = useState<NativeConsentPayload | null>(null)
  // Starts from the server's answer, and is only ever revoked — see the
  // recovery effect below.
  const [isNative, setIsNative] = useState(platform !== null)

  useEffect(() => {
    if (!isNative) return

    const bridge = getNativeBridge()
    if (!bridge) return

    let cancelled = false

    bridge.send("app.ready", { gameId: GAME_ID })

    void getNativeEnvironment().then((resolved) => {
      if (cancelled || !resolved) return
      setEnvironment(resolved)

      if (resolved.gameId && resolved.gameId !== GAME_ID) {
        console.warn(
          `[native] host opened us as "${resolved.gameId}" but this build is "${GAME_ID}"`,
        )
      }
    })

    void bridge
      .request<NativeConsentPayload>("consent.get")
      .then((resolved) => {
        if (!cancelled) setConsent(resolved)
      })
      .catch((error) => {
        console.warn("[native] consent request failed", error)
      })

    const unsubscribeConsent = bridge.on<NativeConsentPayload>(
      "consent.changed",
      (payload) => {
        if (!cancelled) setConsent(payload)
      },
    )

    return () => {
      cancelled = true
      unsubscribeConsent()
    }
  }, [isNative])

  // Deep links arrive both at launch (as `launchUrl` on the environment) and as
  // broadcasts while the game is open. Route them through the in-memory router,
  // because the browser URL stays locked at "/" inside the webview.
  const handleDeepLink = useCallback((rawUrl: string) => {
    let pathname: string
    try {
      pathname = new URL(rawUrl, window.location.origin).pathname
    } catch {
      console.warn("[native] ignoring unparseable deep link", rawUrl)
      return
    }

    const tab = getTopLevelTab(pathname)
    if (!tab) {
      console.warn("[native] deep link does not match a known tab", pathname)
      return
    }

    dispatchSpaNavigate(tab)
  }, [])

  useEffect(() => {
    if (!isNative) return

    const bridge = getNativeBridge()
    if (!bridge) return

    return bridge.on<unknown>("deeplink", (payload) => {
      if (!isNativeDeepLinkPayload(payload)) {
        console.warn("[native] ignoring malformed deeplink", payload)
        return
      }
      handleDeepLink(payload.url)
    })
  }, [isNative, handleDeepLink])

  useEffect(() => {
    if (!isNative || !environment?.launchUrl) return
    handleDeepLink(environment.launchUrl)
  }, [isNative, environment?.launchUrl, handleDeepLink])

  // Recovery path: the server can only infer native mode from a query param or a
  // cookie, so a `?gh_native=1` link opened in a real browser would otherwise
  // stay stuck without site chrome and without a working login. If no bridge
  // shows up, drop the cookie and re-render as an ordinary web page.
  useEffect(() => {
    if (!isNative || getNativeBridge()) return

    const timer = window.setTimeout(() => {
      if (getNativeBridge()) return

      console.warn(
        "[native] no bridge after grace period — falling back to web mode",
      )
      document.cookie = `${NATIVE_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`
      setIsNative(false)
      router.refresh()
    }, BRIDGE_GRACE_MS)

    return () => window.clearTimeout(timer)
  }, [isNative, router])

  const openMenu = useCallback(() => {
    const bridge = getNativeBridge()
    if (!bridge) {
      console.warn("[native] openMenu called outside the native shell")
      return
    }
    bridge.send("menu.open", { gameId: GAME_ID })
  }, [])

  const value = useMemo<NativeContextValue>(
    () => ({
      isNative,
      platform: isNative ? platform : null,
      environment,
      consent,
      openMenu,
    }),
    [isNative, platform, environment, consent, openMenu],
  )

  return (
    <NativeContext.Provider value={value}>{children}</NativeContext.Provider>
  )
}

/**
 * Safe to call from anywhere: outside the provider it reports a plain web
 * document rather than throwing, so components do not need to care.
 */
export function useNative(): NativeContextValue {
  return useContext(NativeContext)
}
